import { useState, useCallback } from "react";
import { rangeToParams } from "../../contexts/DateFilterContext";
import { toISODate } from "./dateRangeUtils";

/**
 * Holds the pending selection while the dropdown is open.
 * Nothing here touches global state until toCommitArgs() is applied by the caller.
 */
export const useDateRangeDraft = (committedKey, committedCustomRange) => {
	const [draftKey, setDraftKey] = useState(committedKey);
	const [draftStart, setDraftStart] = useState(committedCustomRange?.from ? new Date(committedCustomRange.from) : null);
	const [draftEnd, setDraftEnd] = useState(committedCustomRange?.to ? new Date(committedCustomRange.to) : null);

	const reset = useCallback(() => {
		setDraftKey(committedKey);
		setDraftStart(committedCustomRange?.from ? new Date(committedCustomRange.from) : null);
		setDraftEnd(committedCustomRange?.to ? new Date(committedCustomRange.to) : null);
	}, [committedKey, committedCustomRange]);

	const selectPreset = useCallback((key) => {
		setDraftKey(key);
		const params = rangeToParams(key);
		setDraftStart(params.from ? new Date(params.from) : null);
		setDraftEnd(params.to ? new Date(params.to) : null);
	}, []);

	// Two-click calendar selection: first click sets start, second sets end (swaps if reversed)
	const selectDay = useCallback((day) => {
		setDraftKey("custom");
		if (!draftStart || (draftStart && draftEnd)) {
			setDraftStart(day);
			setDraftEnd(null);
			return;
		}
		if (day < draftStart) {
			setDraftEnd(draftStart);
			setDraftStart(day);
		} else {
			setDraftEnd(day);
		}
	}, [draftStart, draftEnd]);

	const isValid = draftKey !== "custom" || Boolean(draftStart && draftEnd);

	const toCommitArgs = () => draftKey === "custom"
		? ["custom", { from: toISODate(draftStart), to: toISODate(draftEnd) }]
		: [draftKey, null];

	return { draftKey, draftStart, draftEnd, reset, selectPreset, selectDay, isValid, toCommitArgs };
};
